import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { login as loginRequest, fetchUserRole } from "../data/api";
import { installHttp } from "./http";
import {
  Role, roleFromToken, roleFromTokenOrNull, normalizeRole,
  usernameFromToken, useridFromToken, displayNameFromToken, statusFromToken,
  tenantFromToken, impersonatingFromToken,
} from "./roles";

const ADMIN_BACKUP_KEY = "admin_token_backup";
const IMP_TENANT_KEY = "imp_tenant_name";

interface AuthState {
  token: string | null;
  role: Role;
  username: string;
  displayName: string;
  userId: number | null;
  tenantId: number | null;
  status: string | null;
  isAuthenticated: boolean;
  /** Phase D: true while a super admin is in a read-only "view-as-tenant" session. */
  impersonating: boolean;
  /** Name of the tenant being inspected (for the banner). */
  impersonationTenant: string | null;
  /** Email of the acting super admin during impersonation. */
  actingAdmin: string | null;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string, preferredRole?: Role) => Promise<Role>;
  logout: () => void;
  setRole: (role: Role) => void;
  /** Enter a read-only tenant inspection session with a server-issued scoped token. */
  enterImpersonation: (token: string, tenantName: string) => void;
  /** Exit tenant inspection and restore the super admin session. */
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function stateFromToken(token: string | null, extra?: Partial<AuthState>): AuthState {
  const impersonating = impersonatingFromToken(token);
  return {
    token,
    role: roleFromToken(token),
    username: usernameFromToken(token),
    displayName: displayNameFromToken(token),
    userId: useridFromToken(token),
    tenantId: tenantFromToken(token),
    status: statusFromToken(token),
    isAuthenticated: !!token,
    impersonating,
    impersonationTenant: impersonating ? (safeGet(IMP_TENANT_KEY) || null) : null,
    actingAdmin: impersonating ? (usernameFromToken(safeGet(ADMIN_BACKUP_KEY)) || null) : null,
    ...extra,
  };
}

function safeGet(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}

function readInitial(): AuthState {
  const token = safeGet("token");
  const storedRole = safeGet("role") as Role | null;
  const base = stateFromToken(token);
  // Prefer a stored role only when the token itself carries no role claim.
  if (storedRole && !roleFromTokenOrNull(token)) base.role = storedRole;
  return base;
}

const EMPTY: AuthState = {
  token: null, role: "BUYER", username: "", displayName: "", userId: null,
  tenantId: null, status: null, isAuthenticated: false,
  impersonating: false, impersonationTenant: null, actingAdmin: null,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(readInitial);

  useEffect(() => {
    installHttp(() => setState(EMPTY));
  }, []);

  const login = useCallback(async (username: string, password: string, preferredRole?: Role): Promise<Role> => {
    const { token, role: bodyRole, status: bodyStatus } = await loginRequest(username, password); // persists token

    let role: Role | null = normalizeRole(bodyRole) ?? roleFromTokenOrNull(token);
    if (!role) role = normalizeRole(await fetchUserRole(username));
    const finalRole: Role = role ?? preferredRole ?? "BUYER";

    try { localStorage.setItem("role", finalRole); } catch { /* ignore */ }
    setState(stateFromToken(token, {
      role: finalRole,
      username: usernameFromToken(token) || username,
      displayName: displayNameFromToken(token) || username,
      status: (bodyStatus ?? statusFromToken(token)) as string | null,
    }));
    return finalRole;
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem(ADMIN_BACKUP_KEY);
      localStorage.removeItem(IMP_TENANT_KEY);
    } catch { /* ignore */ }
    setState(EMPTY);
  }, []);

  const setRole = useCallback((role: Role) => {
    try { localStorage.setItem("role", role); } catch { /* ignore */ }
    setState((s) => ({ ...s, role }));
  }, []);

  const enterImpersonation = useCallback((impToken: string, tenantName: string) => {
    try {
      const current = localStorage.getItem("token");
      if (current) localStorage.setItem(ADMIN_BACKUP_KEY, current);
      localStorage.setItem(IMP_TENANT_KEY, tenantName || "");
      localStorage.setItem("token", impToken);
      localStorage.removeItem("role"); // force role to come from the imp token
    } catch { /* ignore */ }
    setState(stateFromToken(impToken));
  }, []);

  const exitImpersonation = useCallback(() => {
    let admin: string | null = null;
    try {
      admin = localStorage.getItem(ADMIN_BACKUP_KEY);
      if (admin) localStorage.setItem("token", admin);
      localStorage.removeItem(ADMIN_BACKUP_KEY);
      localStorage.removeItem(IMP_TENANT_KEY);
      localStorage.removeItem("role");
    } catch { /* ignore */ }
    setState(admin ? stateFromToken(admin) : EMPTY);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, setRole, enterImpersonation, exitImpersonation }),
    [state, login, logout, setRole, enterImpersonation, exitImpersonation]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
